import { EyeOpenIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { ITriggeredWorkflow } from "../../../../@types/admin/AdminWorkflow";
import { TableSorted } from "../../../../components/TableSorted";
import { useTriggeredWorkflows } from "../../../../hooks/queries/admin/workflows";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  RowActionGroup,
  SearchInput,
  StatusBadge,
} from "../AdminPage.styles";
import { RowAction } from "../RowAction";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";
import { WorkflowDrillDownModal } from "./WorkflowDrillDownModal";
import { humanizeEnumName } from "./workflowStep";

const SEARCH_FIELDS: (keyof ITriggeredWorkflow)[] = [
  "patientName",
  "patientEmail",
  "patientId",
  "wardName",
  "workflowName",
  "status",
];

function runTone(status: string): "active" | "inactive" | "warning" {
  if (status === "Done") return "active";
  if (status === "InProgress") return "warning";
  return "inactive";
}

export interface IWorkflowsPanelProps {
  /**
   * Restrict to one patient. Omit for every patient with a triggered
   * workflow — Retool's `patientId` module prop, blank on the Workflows page
   * and filled in on the patient pages.
   */
  patientId?: number;
  title?: string;
  /** Rows per page. Defaults to the shared /admin page size. */
  pageSize?: number;
}

/**
 * Patients with a triggered workflow — the portal port of Retool's
 * `moduleWorkflows` (EN-5167).
 *
 * Shipped as a shared component because Retool embedded the same module in
 * the Patients and Archived Patients pages; **EN-5170 and EN-5171 embed
 * this**, they do not inline another workflows table.
 *
 *   <WorkflowsPanel />                        // every patient
 *   <WorkflowsPanel patientId={patient.id} /> // one patient's
 *
 * Read-only: workflows are authored elsewhere. This is the "what happened"
 * view — which patients a workflow triggered for, and what ran on each run.
 */
export function WorkflowsPanel({
  patientId,
  title = "Triggered workflows",
  pageSize,
}: IWorkflowsPanelProps) {
  const { openModal } = useModal();
  const { triggeredWorkflows, isLoading, isError } =
    useTriggeredWorkflows(patientId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: triggeredWorkflows,
    defaultPageSize: pageSize,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<ITriggeredWorkflow, string>[] = useMemo(
    () => [
      { accessorKey: "patientName", header: "Patient", enableSorting: true },
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (row) => String(row.patientId),
        enableSorting: true,
      },
      {
        // cms_patient.hospital_id is the patient's PRIMARY ward only — since
        // EN-4960 a patient can belong to several. The header says so.
        accessorKey: "wardName",
        header: "Primary ward",
        enableSorting: true,
      },
      { accessorKey: "workflowName", header: "Workflow", enableSorting: true },
      {
        id: "status",
        header: "Run status",
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusBadge status={runTone(row.original.status)}>
            {humanizeEnumName(row.original.status)}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "published",
        header: "Definition",
        accessorFn: (row) => (row.published ? "Published" : "Unpublished"),
        enableSorting: true,
      },
      {
        accessorKey: "lastUpdated",
        header: "Last updated",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUpdated, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="View runs"
              icon={<EyeOpenIcon />}
              onClick={() => {
                openModal(<WorkflowDrillDownModal workflow={row.original} />, {
                  width: "min(1100px, 96vw)",
                  maxHeight: "90vh",
                  showCloseButton: true,
                });
              }}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
      </PanelHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by patient, ward, workflow or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the triggered workflows. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-triggered-workflows"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "workflows",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PanelContainer>
  );
}
